
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    InputBase,
    IconButton,
    List,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Divider,
    Badge,
    Tooltip,
    TextField,
    CircularProgress,
    Tabs,
    Tab,
    Menu,
    MenuItem
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    Edit as EditIcon,
    MoreHoriz as MoreIcon,
    AttachFile as AttachFileIcon,
    EmojiEmotions as EmojiIcon,
    Send as SendIcon,
    VideoCall as VideoCallIcon,
    Call as CallIcon,
    Add as AddIcon,
    Image as ImageIcon,
    Gif as GifIcon,
    StickyNote2 as StickerIcon,
    FormatBold as FormatIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import client from '../../api/client';
import { io } from 'socket.io-client';

// --- Styled Components for "Teams" Look ---
const TeamsSidebar = styled(Box)(({ theme }) => ({
    width: 320,
    backgroundColor: '#f5f5f5', // Teams-like light gray
    borderRight: '1px solid #e1dfdd',
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 80px)', // Adjust based on MainLayout header
}));

const TeamsChatArea = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 80px)',
}));

const StatusBadge = styled(Badge)(({ status }) => ({
    '& .MuiBadge-badge': {
        backgroundColor:
            status === 'online' ? '#92c353' :
                status === 'busy' ? '#c4314b' :
                    status === 'away' ? '#f8d22a' :
                        '#747474',
        color:
            status === 'online' ? '#92c353' :
                status === 'busy' ? '#c4314b' :
                    status === 'away' ? '#f8d22a' :
                        '#747474',
        boxShadow: `0 0 0 2px #fff`,
        '&::after': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '1px solid currentColor',
            content: '""',
        },
    },
}));

// Teams Message Bubble
const MessageBubble = styled(Box)(({ isOwn }) => ({
    maxWidth: '65%',
    padding: '8px 12px',
    borderRadius: isOwn ? '4px 4px 0 4px' : '0 4px 4px 4px',
    backgroundColor: isOwn ? '#e8ebfa' : '#ffffff', // Teams utilize light distinct backgrounds
    // Teams 'My' messages are often light purple/blue-ish in new themes, or plain. Let's stick to a clean look.
    // Actually, strictly Teams: 'Me' is usually right, 'Theirs' left.
    // Colors: Theirs = transparent/white with details? Or plain text?
    // Let's go: My = #f0ebf8 (light purple), Theirs = #fff.
    border: isOwn ? 'none' : '1px solid #e1dfdd',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    marginBottom: '4px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
}));

// --- Main Component ---
const ChatPage = () => {
    // State
    const [conversations, setConversations] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [users, setUsers] = useState([]);
    const [activeTab, setActiveTab] = useState(0); // 0=Chat, 1=Files

    // Refs
    const messagesEndRef = useRef(null);
    const selectedChatRef = useRef(null);
    const socketRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    // Initial Fetch
    const fetchConversations = async () => {
        try {
            const params = {};
            if (searchQuery) params.search = searchQuery;
            const res = await client.get('/chat/conversations', { params });
            if (res.success) {
                // Dedup backend results by otherUserId just in case
                const unique = Array.from(new Map(res.data.map(c => [c.otherUserId, c])).values());
                setConversations(unique);
            }
        } catch (err) {
            console.error('Error fetching conversations:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (chatId) => {
        setMessagesLoading(true);
        try {
            const res = await client.get(`/chat/messages/${chatId}`);
            if (res.success) {
                setMessages(res.data);
                setConversations(prev => prev.map(c => c.id === chatId ? { ...c, unreadCount: 0 } : c));
            }
        } catch (err) {
            console.error('Error fetching messages:', err);
        } finally {
            setMessagesLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const params = {};
            if (searchQuery) params.search = searchQuery;
            const res = await client.get('/chat/users', { params });
            if (res.success) {
                setUsers(res.data);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    // Effects
    useEffect(() => {
        selectedChatRef.current = selectedChat;
    }, [selectedChat]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;

        const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://65.1.61.204:3000';
        const socketUrl = apiBaseUrl.replace('/api', '');

        socketRef.current = io(socketUrl, {
            auth: { token },
            path: '/socket.io',
            transports: ['polling', 'websocket'],
        });

        socketRef.current.on('connect', () => {
            client.put('/chat/presence', { status: 'online' });
            fetchConversations();
            if (selectedChatRef.current) {
                socketRef.current.emit('join_chat', selectedChatRef.current.id);
            }
        });

        socketRef.current.on('message_received', (message) => {
            const currentChat = selectedChatRef.current;
            if (currentChat && message.chat_id === currentChat.id) {
                setMessages(prev => [...prev, message]);
            }
            fetchConversations();
        });

        socketRef.current.on('presence_update', ({ userId, status }) => {
            setConversations(prev => prev.map(c =>
                c.otherUserId === userId ? { ...c, otherUserStatus: status } : c
            ));
        });

        return () => {
            if (socketRef.current) {
                client.put('/chat/presence', { status: 'offline' });
                socketRef.current.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        if (selectedChat && socketRef.current) {
            socketRef.current.emit('join_chat', selectedChat.id);
            fetchMessages(selectedChat.id);
        }
    }, [selectedChat]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchQuery) fetchUsers();
            fetchConversations();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Handlers
    const handleSendMessage = async () => {
        if (!messageInput.trim() || !selectedChat) return;

        try {
            const res = await client.post('/chat/messages', {
                chatId: selectedChat.id,
                content: messageInput,
                type: 'text'
            });
            if (res.success) {
                const newMessage = res.data;
                setMessages(prev => [...prev, newMessage]);
                setMessageInput('');
                if (socketRef.current) {
                    socketRef.current.emit('new_message', newMessage);
                }
                setConversations(prev => prev.map(c =>
                    c.id === selectedChat.id
                        ? { ...c, lastMessage: messageInput, lastMessageTime: new Date().toISOString() }
                        : c
                ));
            }
        } catch (err) {
            console.error('Error sending message:', err);
        }
    };

    const handleChatSelect = async (chat) => {
        if (chat.id) {
            setSelectedChat(chat);
        } else {
            try {
                const res = await client.post('/chat/direct', { otherUserId: chat.otherUserId });
                if (res.success) {
                    const newChat = { ...chat, id: res.chatId };
                    setConversations(prev => {
                        // Prevent duplicates
                        if (prev.some(c => c.otherUserId === newChat.otherUserId)) return prev;
                        return [newChat, ...prev];
                    });
                    setSelectedChat(newChat);
                }
            } catch (err) {
                console.error('Error creating chat:', err);
            }
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatLastSeen = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    };

    // Filter logic
    const peopleResults = users.filter(u => {
        // Exclude current conversations
        return !conversations.some(c => c.otherUserId === u.id);
    });

    // Ensure rendering unique conversations
    const uniqueConversations = Array.from(new Map(conversations.map(c => [c.otherUserId, c])).values());

    return (
        <Box sx={{ display: 'flex', height: '88vh', fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif' }}>

            {/* --- Sidebar (Chat List) --- */}
            <TeamsSidebar>
                {/* Header — no "Chat" title, the top bar already names the page */}
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Box>
                        <IconButton size="small"><FilterIcon fontSize="small" /></IconButton>
                        <IconButton size="small"><EditIcon fontSize="small" /></IconButton>
                    </Box>
                </Box>

                {/* Search */}
                <Box sx={{ px: 2, pb: 1 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: '2px 4px',
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: '#fff',
                            border: '1px solid #e1dfdd',
                            borderRadius: 1,
                            height: 32
                        }}
                    >
                        <InputBase
                            sx={{ ml: 1, flex: 1, fontSize: '0.9rem' }}
                            placeholder="Filter by name"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <IconButton size="small" sx={{ p: '5px' }}>
                            <SearchIcon fontSize="small" />
                        </IconButton>
                    </Paper>
                </Box>

                {/* Conversation List */}
                <List sx={{ flex: 1, overflowY: 'auto', pt: 0 }}>
                    {/* Placeholder for "Pinned" if needed */}

                    {/* "Request" placeholder from image */}
                    {/* <ListItemButton sx={{ py: 1, px: 2, '&:hover': { bgcolor: '#f0f0f0' } }}>
                         <ListItemAvatar sx={{ minWidth: 40 }}>
                             <Badge badgeContent={1} color="warning" overlap="circular">
                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#f8d22a', color: '#000', fontSize: '0.8rem' }}>Req</Avatar>
                             </Badge>
                         </ListItemAvatar>
                         <ListItemText primary={<Typography variant="body2" fontWeight={600}>1 request</Typography>} />
                     </ListItemButton> */}

                    <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: '#616161', fontWeight: 600 }}>
                        Recent
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} /></Box>
                    ) : (
                        <>
                            {uniqueConversations.map((chat) => (
                                <ListItemButton
                                    key={chat.id || chat.otherUserId}
                                    selected={selectedChat?.id === chat.id}
                                    onClick={() => handleChatSelect(chat)}
                                    sx={{
                                        py: 1,
                                        px: 2,
                                        '&.Mui-selected': {
                                            bgcolor: '#ffffff',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                                            borderLeft: 'none', // Teams doesn't typically do the left border, but highlights background
                                            '&:hover': { bgcolor: '#ffffff' }
                                        },
                                        '&:hover': { bgcolor: '#e1dfdd' }
                                    }}
                                >
                                    <ListItemAvatar sx={{ minWidth: 48 }}>
                                        <StatusBadge
                                            overlap="circular"
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            variant="dot"
                                            status={chat.otherUserStatus}
                                        >
                                            <Avatar sx={{ width: 36, height: 36, bgcolor: '#6264A7', fontSize: '0.9rem' }}>
                                                {(chat.otherUserName ? chat.otherUserName : (chat.name || '?'))[0].toUpperCase()}
                                            </Avatar>
                                        </StatusBadge>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" sx={{ fontWeight: selectedChat?.id === chat.id ? 700 : 400, color: '#242424' }}>
                                                    {chat.otherUserName ? chat.otherUserName : (chat.name || 'Unknown')}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#616161', fontSize: '0.7rem' }}>
                                                    {formatTime(chat.lastMessageTime)}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: '100%' }}>
                                                {chat.lastMessage || 'No messages yet'}
                                            </Typography>
                                        }
                                    />
                                </ListItemButton>
                            ))}

                            {/* People Results */}
                            {searchQuery && peopleResults.length > 0 && (
                                <>
                                    <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: '#616161', fontWeight: 600 }}>
                                        Suggested People
                                    </Typography>
                                    {peopleResults.map((user) => (
                                        <ListItemButton
                                            key={user.id}
                                            onClick={() => handleChatSelect({ ...user, otherUserId: user.id, otherUserName: user.name })}
                                            sx={{ py: 1, px: 2 }}
                                        >
                                            <ListItemAvatar sx={{ minWidth: 48 }}>
                                                <Avatar sx={{ width: 36, height: 36, bgcolor: '#8b8b8b', fontSize: '0.9rem' }}>
                                                    {user.name[0].toUpperCase()}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={user.name}
                                                secondary={user.department}
                                            />
                                        </ListItemButton>
                                    ))}
                                </>
                            )}

                            {/* No Results Feedback */}
                            {searchQuery && uniqueConversations.length === 0 && peopleResults.length === 0 && !loading && (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">No results for "{searchQuery}"</Typography>
                                </Box>
                            )}
                        </>
                    )}
                </List>
            </TeamsSidebar>

            {/* --- Main Area --- */}
            <TeamsChatArea>
                {selectedChat ? (
                    <>
                        {/* Chat Header */}
                        <Box sx={{
                            height: 60,
                            px: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: '1px solid #e1dfdd'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#6264A7' }}>
                                    {(selectedChat.otherUserName ? selectedChat.otherUserName : (selectedChat.name || '?'))[0].toUpperCase()}
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                        {selectedChat.otherUserName ? selectedChat.otherUserName : (selectedChat.name || 'Unknown')}
                                    </Typography>

                                    {/* Tabs */}
                                    <Tabs
                                        value={activeTab}
                                        onChange={(e, v) => setActiveTab(v)}
                                        sx={{
                                            minHeight: 30,
                                            '& .MuiTab-root': {
                                                textTransform: 'none',
                                                minHeight: 30,
                                                padding: '0 8px',
                                                fontSize: '0.85rem',
                                                mr: 1
                                            },
                                            '& .MuiTabs-indicator': {
                                                backgroundColor: '#6264A7'
                                            }
                                        }}
                                    >
                                        <Tab label="Chat" />
                                        <Tab label="Files" />
                                        <Tab label="Photos" />
                                    </Tabs>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton sx={{ color: '#6264A7' }}><VideoCallIcon /></IconButton>
                                <IconButton sx={{ color: '#6264A7' }}><CallIcon /></IconButton>
                                <IconButton size="small"><SearchIcon /></IconButton>
                                <IconButton size="small"><AddIcon /></IconButton>
                                <IconButton size="small"><MoreIcon /></IconButton>
                            </Box>
                        </Box>

                        {/* Messages List */}
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: '#ffffff' }}>
                            {activeTab === 0 ? (
                                <>
                                    {messages.map((msg, index) => {
                                        const isOwn = msg.sender_id === JSON.parse(localStorage.getItem('user'))?.id;
                                        // Simple grouping: if prev msg same sender, hide avatar
                                        const prevMsg = index > 0 ? messages[index - 1] : null;
                                        const showHeader = !prevMsg || prevMsg.sender_id !== msg.sender_id;

                                        return (
                                            <Box
                                                key={msg.id}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                                                    mb: 1,
                                                    gap: 1
                                                }}
                                            >
                                                {!isOwn && (
                                                    <Box sx={{ width: 32 }}>
                                                        {showHeader && (
                                                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#6264A7', fontSize: '0.8rem' }}>
                                                                {msg.senderName?.[0]}
                                                            </Avatar>
                                                        )}
                                                    </Box>
                                                )}

                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start', maxWidth: '75%' }}>
                                                    {showHeader && !isOwn && (
                                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, ml: 1, mb: 0.5 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#424242' }}>
                                                                {msg.senderName}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#616161', fontSize: '0.7rem' }}>
                                                                {formatLastSeen(msg.created_at)}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    <MessageBubble isOwn={isOwn}>
                                                        <Typography variant="body1" sx={{ fontSize: '0.95rem', color: '#242424' }}>
                                                            {msg.content}
                                                        </Typography>
                                                        {isOwn && (
                                                            <Typography variant="caption" sx={{ textAlign: 'right', display: 'block', fontSize: '0.65rem', color: '#616161', mt: 0.5 }}>
                                                                {isOwn ? 'Sent' : ''} {formatTime(msg.created_at)}
                                                            </Typography>
                                                        )}
                                                    </MessageBubble>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                    <div ref={messagesEndRef} />
                                </>
                            ) : (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography color="text.secondary">No files shared yet.</Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Message Input - Teams Style */}
                        <Box sx={{ p: 2, pt: 0 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    border: '1px solid #d1d1d1',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                    '&:focus-within': {
                                        borderColor: '#6264A7',
                                        borderWidth: '2px',
                                        m: '-1px' // Prevent layout shift
                                    }
                                }}
                            >
                                <InputBase
                                    multiline
                                    minRows={2}
                                    maxRows={6}
                                    placeholder="Type a message"
                                    value={messageInput}
                                    onChange={(e) => setMessageInput(e.target.value)}
                                    // Submit on Enter (prevent default unless Shift)
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                    sx={{ p: 1.5, fontSize: '0.95rem' }}
                                />
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 1,
                                    bgcolor: '#fcfcfc',
                                    borderTop: '1px solid #f0f0f0'
                                }}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Tooltip title="Format"><IconButton size="small"><FormatIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Attach"><IconButton size="small"><AttachFileIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Emoji"><IconButton size="small"><EmojiIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Giphy"><IconButton size="small"><GifIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Sticker"><IconButton size="small"><StickerIcon fontSize="small" /></IconButton></Tooltip>
                                    </Box>
                                    <IconButton
                                        color="primary"
                                        onClick={handleSendMessage}
                                        disabled={!messageInput.trim()}
                                        sx={{
                                            color: messageInput.trim() ? '#6264A7' : '#c8c8c8',
                                            transform: 'rotate(-45deg)', // Teams send icon is angled
                                            mt: -0.5
                                        }}
                                    >
                                        <SendIcon />
                                    </IconButton>
                                </Box>
                            </Paper>
                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: '#616161' }}>
                                Press Enter to send
                            </Typography>
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#616161' }}>
                        <Typography variant="h5" fontWeight={600} gutterBottom>
                            Welcome to Chat
                        </Typography>
                        <Typography color="text.secondary">
                            Select a conversation from the left to start chatting.
                        </Typography>
                    </Box>
                )}
            </TeamsChatArea>
        </Box>
    );
};

export default ChatPage;
